

import { useState, useEffect } from "react";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { eventsApi, mastersApi } from "@/lib/api";
import SearchableDropdown from "../../components/SearchableDropdown";

interface ServiceOption {
  id: number;
  name: string;
  description?: string;
  unit_price?: number;
  cost_price?: number;
  base_price?: number;
  selling_price?: number;
  vendor?: {
    id: number;
    name: string;
  };
}

interface AddServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onServiceAdded: () => void;
}

const INRIcon = ({ className }: { className?: string }) => (
  <span className={className}>₹</span>
);

export default function AddServiceModal({
  isOpen,
  onClose,
  eventId,
  onServiceAdded,
}: AddServiceModalProps) {
  const { showToast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [services, setServices] = useState<ServiceOption[]>([]);

  // Check if user is admin or master
  const canViewProfit = user?.role === 'admin' || user?.role === 'master';

  // Form state - matching quotation style
  const [formData, setFormData] = useState({
    service_id: undefined as number | undefined,
    description: "",
    quantity: 1,
    unit_price: 0,
    cost_price: 0,
    notes: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof typeof formData, string>>>({});

  // Fetch services when modal opens
  useEffect(() => {
    if (isOpen) {
      loadServices();
      resetForm();
    }
  }, [isOpen]);

  const loadServices = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.services.list();
      // Handle different API response structures
      let serviceData = [];
      if (Array.isArray(response)) {
        serviceData = response;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        serviceData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        serviceData = response.data;
      } else if (response?.services && Array.isArray(response.services)) {
        serviceData = response.services;
      }
      setServices(serviceData);
    } catch (error) {
      console.error("Error loading services:", error);
      showToast("Failed to load services", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      service_id: undefined,
      description: "",
      quantity: 1,
      unit_price: 0,
      cost_price: 0,
      notes: "",
    });
    setErrors({});
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleServiceSelect = (serviceId: string) => {
    const service = services.find(s => String(s.id) === serviceId);
    if (service) {
      setFormData(prev => ({
        ...prev,
        service_id: service.id,
        description: service.name,
        unit_price: service.selling_price || service.unit_price || 0,
        cost_price: service.base_price || service.cost_price || 0,
      }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof typeof formData, string>> = {};

    if (!formData.description?.trim()) newErrors.description = "Description is required";
    if (formData.quantity <= 0) newErrors.quantity = "Quantity must be greater than 0";
    if (formData.unit_price < 0) newErrors.unit_price = "Price cannot be negative";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSubmitting(true);
    try {
      await eventsApi.addEventService(eventId, {
        service_id: formData.service_id,
        description: formData.description,
        quantity: formData.quantity,
        unit_price: formData.unit_price,
        cost_price: formData.cost_price,
        notes: formData.notes,
      });

      showToast("Service added successfully", "success");
      onClose();
      onServiceAdded();
    } catch (error: any) {
      showToast(error?.message || "Failed to add service", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const lineTotal = formData.quantity * formData.unit_price;
  const lineCost = formData.quantity * formData.cost_price;
  const lineProfit = lineTotal - lineCost;
  const lineMargin = lineTotal > 0 ? Math.round((lineProfit / lineTotal) * 100) : 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-stone-900/60 backdrop-blur-sm">
      <div className="w-full h-full bg-white flex flex-col md:max-w-2xl md:mx-auto md:my-8 md:rounded-2xl md:shadow-2xl md:overflow-hidden md:h-auto md:max-h-[90vh]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-stone-200 bg-gradient-to-r from-violet-50 to-white flex-shrink-0 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-stone-800">Add Service</h3>
            <p className="text-[10px] text-stone-500">Select service and pricing</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Service Selection */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Service</label>
            {loading ? (
              <div className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-stone-50 text-sm text-stone-400">
                Loading services...
              </div>
            ) : (
              <SearchableDropdown
                options={services.map(s => ({
                  value: String(s.id),
                  label: s.vendor?.name 
                    ? `${s.name} - ₹${s.selling_price || s.unit_price || 0} by ${s.vendor.name}`
                    : `${s.name} - ₹${s.selling_price || s.unit_price || 0}`,
                }))}
                value={formData.service_id ? String(formData.service_id) : ""}
                onChange={handleServiceSelect}
                placeholder="Select service"
              />
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Service description"
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
            />
            {errors.description && <p className="text-xs text-rose-500 mt-0.5">{errors.description}</p>}
          </div>

          {/* Quantity & Price Row */}
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Qty *</label>
              <input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", parseInt(e.target.value) || 1)}
                className="w-full px-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
              />
              {errors.quantity && <p className="text-xs text-rose-500 mt-0.5">{errors.quantity}</p>}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-stone-700 mb-1">Unit Price *</label>
              <div className="relative">
                <INRIcon className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-xs" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => handleChange("unit_price", parseFloat(e.target.value) || 0)}
                  className="w-full pl-5 pr-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
                />
              </div>
              {errors.unit_price && <p className="text-xs text-rose-500 mt-0.5">{errors.unit_price}</p>}
            </div>
          </div>

          {/* Cost Price */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-1">Cost Price/Unit</label>
              <div className="relative">
                <INRIcon className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400 font-bold text-xs" />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => handleChange("cost_price", parseFloat(e.target.value) || 0)}
                  className="w-full pl-5 pr-2 py-2 rounded-lg border border-stone-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none"
                />
              </div>
            </div>
            <div className="bg-stone-50 rounded-lg p-2 border border-stone-200">
              <p className="text-[10px] text-stone-500">Profit</p>
              <p className={`text-sm font-bold ${lineProfit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                ₹{lineProfit.toLocaleString('en-IN')} ({lineMargin}%)
              </p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-stone-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Special requirements..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-stone-200 text-sm focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 bg-stone-50 px-4 py-3 flex gap-2 flex-shrink-0">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-3 rounded-lg bg-white text-stone-600 font-semibold text-sm border border-stone-200 hover:bg-stone-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 px-4 py-3 rounded-lg bg-violet-600 text-white font-semibold text-sm hover:bg-violet-700 transition-colors disabled:opacity-50"
          >
            {submitting ? "Adding..." : "Add Service"}
          </button>
        </div>
      </div>
    </div>
  );
}
